/**
 * Nome das etapas do funil, por id de status.
 *
 * O nome do funil mora no front: a tabela `status` da API é decorativa e diverge
 * (a mesma conversa que o funil chama de "Em qualificação" tem descrição
 * "Não Iniciado" no banco) — ver docs/propostas/status-arquitetura.html.
 *
 * Hoje existem cópias deste mapa em PipelinePage, DealDetailsPage, TrilhoCanvas,
 * EscolherTrilhoModal, CreateOpportunityPanel e NotificationsBell. Este arquivo é
 * o lugar para onde elas devem convergir; quem nasce novo já usa daqui.
 */
/**
 * O nome que o produto entrega. Desde que a empresa pode desenhar o próprio funil, isto é só o
 * **ponto de partida**: quem manda é o que vem de `GET /api/Funil`, carregado uma vez por sessão
 * em `definirEtapasDaEmpresa`. Mantido aqui para a tela não ficar sem rótulo enquanto a lista
 * não chega — e para o app seguir funcionando se a chamada falhar.
 */
export const NOME_ETAPA: Record<number, string> = {
    1: 'Novo',
    2: 'Em qualificação',
    3: 'Ganho',
    4: 'Proposta enviada',
    5: 'Em negociação',
    6: 'Perdido',
    8: 'IA encerrada',
};

/** O que a empresa chamou cada etapa. Vazio até a primeira carga. */
let nomesDaEmpresa: Record<number, string> = {};

/**
 * Guarda o funil da empresa. Chamado depois do login, no mesmo lugar onde as permissões chegam.
 * As seis telas que mostram nome de etapa passam a ler daqui sem mudar uma linha.
 */
/** Avisa quem desenha coluna que a lista mudou — o quadro escuta para redesenhar. */
export const FUNIL_CARREGADO_EVENT = 'brik:funil-carregado';

export const definirEtapasDaEmpresa = (etapas: { id: number; nome: string; papel: number | null; entradaDoLead?: boolean }[]) => {
    nomesDaEmpresa = Object.fromEntries(etapas.map(e => [e.id, e.nome]));
    gruposDaEmpresa = montarGrupos(etapas);
    entradaDaEmpresa = etapas.find(e => e.entradaDoLead)?.id ?? null;
    window.dispatchEvent(new Event(FUNIL_CARREGADO_EVENT));
};

/** A etapa marcada como "Entrada do lead" no funil da empresa. Nula até a primeira carga. */
let entradaDaEmpresa: number | null = null;

/**
 * Onde o lead cai quando chega para o time — e onde a oportunidade manual nasce sem escolha.
 * Toda empresa tem uma (a API grava); antes do funil chegar, a primeira etapa do miolo.
 */
export const etapaDeEntrada = (): number =>
    entradaDaEmpresa ?? gruposDoFunil().find(g => g.plus)?.alvo ?? 2;

/** O miolo do funil da empresa, na ordem dela: as etapas entre o robô e os desfechos. */
export const etapasDoMiolo = (): number[] => gruposDoFunil().filter(g => g.plus).map(g => g.alvo);

/**
 * Já saiu da porta e ainda não fechou: etapa do miolo que não é a entrada. A mesma régua do
 * "em andamento" do Painel e da RD na API (`CatalogoEtapas.EmAndamento`).
 */
export const emAndamento = (statusId?: number | null): boolean =>
    !!statusId && statusId !== etapaDeEntrada() && etapasDoMiolo().includes(statusId);

/** O rótulo da etapa, ou traço quando a tarefa não tem oportunidade. */
export const nomeEtapa = (statusId?: number | null) =>
    statusId ? (nomesDaEmpresa[statusId] ?? NOME_ETAPA[statusId] ?? '—') : '—';

/* ── As colunas do quadro ──────────────────────────────────────────────────
   O quadro desenha o funil, então as colunas saem da mesma lista. O formato é
   o que a Pipeline já usava — o que mudou é de onde ele vem. */

export type CorEtapa = 'ia' | 'qual' | 'prop' | 'neg' | 'win' | 'lost' | 'extra';

export interface GrupoEtapa {
    id: string;
    title: string;
    /** Status da API que caem nesta coluna. */
    statusIds: number[];
    /** Status gravado quando um cartão é solto aqui. */
    alvo: number;
    cor: CorEtapa;
    /** "+" no cabeçalho: só onde uma oportunidade manual pode nascer. */
    plus: boolean;
    recolhivel?: boolean;
}

/**
 * O que é coluna (decidido em 2026-09-12): Novos junta Atendimento IA e IA
 * encerrada — é o sistema que passa de 1 para 8, ninguém arrasta entre elas,
 * e o Analytics já soma os dois como "Novos". Ganho fica como coluna para não
 * perder a essência de "concluído"; Perdido nasce recolhida.
 *
 * Vale enquanto o funil da empresa não chega — e se ele não chegar.
 */
export const GRUPOS_PADRAO: GrupoEtapa[] = [
    { id: 'novos', title: 'Novos', statusIds: [1, 8], alvo: 1, cor: 'ia', plus: false },
    { id: 'qualificacao', title: 'Em qualificação', statusIds: [2], alvo: 2, cor: 'qual', plus: true },
    { id: 'proposta', title: 'Proposta enviada', statusIds: [4], alvo: 4, cor: 'prop', plus: true },
    { id: 'negociacao', title: 'Em negociação', statusIds: [5], alvo: 5, cor: 'neg', plus: true },
    { id: 'ganho', title: 'Ganho', statusIds: [3], alvo: 3, cor: 'win', plus: false },
    { id: 'perdido', title: 'Perdido', statusIds: [6], alvo: 6, cor: 'lost', plus: false, recolhivel: true },
];

let gruposDaEmpresa: GrupoEtapa[] | null = null;

/** As colunas de agora: as da empresa, ou as que o produto entrega. */
export const gruposDoFunil = (): GrupoEtapa[] => gruposDaEmpresa ?? GRUPOS_PADRAO;

/** Papéis, espelhando `PapelEtapaEnum` da API — o que decide comportamento. */
const PAPEL_ROBO_ATENDENDO = 1, PAPEL_ROBO_ENCERRADO = 2, PAPEL_MIOLO = 3, PAPEL_GANHO = 4, PAPEL_PERDIDO = 5;

/* A cor de uma etapa criada pela empresa vem do lugar dela na fila. */
const CORES_DO_MIOLO: CorEtapa[] = ['qual', 'prop', 'neg', 'extra'];

const montarGrupos = (etapas: { id: number; nome: string; papel: number | null }[]): GrupoEtapa[] => {
    const doRobo = etapas.filter(e => e.papel === PAPEL_ROBO_ATENDENDO || e.papel === PAPEL_ROBO_ENCERRADO);
    const miolo = etapas.filter(e => e.papel === PAPEL_MIOLO);
    const ganho = etapas.find(e => e.papel === PAPEL_GANHO);
    const perdido = etapas.find(e => e.papel === PAPEL_PERDIDO);

    // Sem as pontas não há funil que se desenhe: melhor o quadro de sempre que um quebrado.
    if (doRobo.length === 0 || miolo.length === 0 || !ganho || !perdido) return GRUPOS_PADRAO;

    return [
        { id: 'novos', title: 'Novos', statusIds: doRobo.map(e => e.id), alvo: doRobo[0].id, cor: 'ia', plus: false },
        ...miolo.map((e, i) => ({
            id: `etapa-${e.id}`,
            title: e.nome,
            statusIds: [e.id],
            alvo: e.id,
            cor: CORES_DO_MIOLO[i % CORES_DO_MIOLO.length],
            plus: true,
        })),
        { id: 'ganho', title: ganho.nome, statusIds: [ganho.id], alvo: ganho.id, cor: 'win' as CorEtapa, plus: false },
        { id: 'perdido', title: perdido.nome, statusIds: [perdido.id], alvo: perdido.id, cor: 'lost' as CorEtapa, plus: false, recolhivel: true },
    ];
};

/**
 * De onde o lead veio (`PlataformaEnum` da API). Mesmo caso das etapas: o nome
 * que a pessoa lê é decisão do produto, não do banco.
 */
export const NOME_ORIGEM: Record<number, string> = {
    1: 'WhatsApp',
    2: 'WhatsApp',
    3: 'Instagram',
    4: 'Messenger',
    5: 'Lead Ads',
    6: 'Cadastro manual',
};

export const nomeOrigem = (origemId?: number | null) =>
    origemId ? (NOME_ORIGEM[origemId] ?? 'Outra origem') : null;

/**
 * A palavra do canal como as TELAS DE TRABALHO a escrevem — a mesma da coluna Origem das
 * Oportunidades e dos filtros do Painel. Difere do `NOME_ORIGEM` acima de propósito: ali o 6 é
 * "Cadastro manual" (a frase de um aviso), aqui é "Manual" (um rótulo de filtro), e aqui existem
 * o 7 e o 8, que nasceram depois.
 *
 * **As duas conexões de WhatsApp (1 Meta e 2 Evolution) são UM canal.** Quem filtra procura
 * "WhatsApp", não "qual integração" — e a `nomeDoCanal` da API devolve "WhatsApp" para tudo que
 * não é Instagram nem Messenger, então agrupar pelo id da plataforma aqui é o que impede duas
 * opções com o mesmo nome no filtro.
 */
export const PALAVRA_CANAL: Record<number, string> = {
    1: 'WhatsApp',
    2: 'WhatsApp',
    3: 'Instagram',
    4: 'Messenger',
    5: 'Lead Ads',
    6: 'Manual',
    7: 'RD Station',
    8: 'Webhook',
};

/** Sem plataforma conhecida é "Conversa": entrou por algum lugar que o produto ainda não nomeia. */
export const palavraDoCanal = (id?: number | null) => (id ? (PALAVRA_CANAL[id] ?? 'Conversa') : 'Conversa');
